"use client";
import { KeyboardEvent, MouseEvent, useContext, useState } from "react";
import { useRouter } from "next/navigation";
import Image from '@components/common/ImageFallback';
import CartIcon from '@components/common/CartIcon';
import { DataContextCart } from '@root/src/contextcart';

export interface Product {
  id: number | string;
  name: string;
  price: string;
  img: string;
  slug?: string;
  rawPrice?: number;
  totalQuantity?: number;
}

interface ProductCardProps {
  product: Product;
  parentSlug?: string;
  priority?: boolean;
}

export default function ProductCard({ product, parentSlug, priority = false }: ProductCardProps) {
  const [added, setAdded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const router = useRouter();
  const contextCart: any = useContext(DataContextCart);
  const { dataCart = [], updateCart } = contextCart || {};
  const totalQuantity = Number(product.totalQuantity || 0);
  const isOutOfStock = totalQuantity <= 0;
  const cartQuantity = Number(
    (dataCart || []).find((item: any) => item.productId === product.id)?.quantity || 0
  );
  const isAtStockLimit = !isOutOfStock && cartQuantity >= totalQuantity;

  const productHref = product.slug
    ? parentSlug
      ? `/${parentSlug}/${product.slug}`
      : `/product/${product.slug}`
    : '';

  const handleCardClick = () => {
    if (!productHref) return;
    router.push(productHref);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!productHref || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    router.push(productHref);
  };

  const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isOutOfStock || isAtStockLimit) return;
    let currentCart = [...(dataCart || [])];
    const priceNumber = Number(String(product.price).replace(/\D/g, '')) || 0;
    const newProduct = {
      productName: product.name,
      discount: 0,
      productPrice: priceNumber,
      productImage: product.img || '',
      quantity: 1,
      productId: product.id,
    };
    const existingIndex = currentCart.findIndex(
      (item: any) => item.productId === newProduct.productId
    );
    if (existingIndex >= 0) {
      currentCart[existingIndex].quantity += 1;
    } else {
      currentCart.push(newProduct);
    }
    updateCart?.(currentCart);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <div
      className="product-card"
      role={productHref ? 'link' : undefined}
      tabIndex={productHref ? 0 : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="product-image">
        {product.img && !imgError ? (
          <Image
            src={product.img}
            alt={product.name}
            className="product-photo"
            width={400}
            height={400}
            sizes="(max-width: 639px) 50vw, (max-width: 900px) 33vw, (max-width: 1199px) 25vw, 20vw"
            priority={priority}
            isLCP={priority}
            onError={() => setImgError(true)}
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <div className="image-placeholder">
            <span className="tea-emoji">🍵</span>
            <div className="img-bg" />
          </div>
        )}
      </div>
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <div className="product-footer">
          <div className="product-meta">
            <span className="product-price">{product.price}</span>
            <span className={`product-stock ${isOutOfStock ? 'out' : ''}`}>
              {isOutOfStock
                ? "Liên hệ 0931 338 566"
                : isAtStockLimit
                  ? `Đã thêm tối đa: ${totalQuantity}`
                  : `Còn lại: ${totalQuantity}`}
            </span>
          </div>
          <button
            className={`add-btn ${added ? "added" : ""}`}
            onClick={handleAdd}
            aria-label={`Thêm ${product.name} vào giỏ`}
            disabled={isOutOfStock || isAtStockLimit}
          >
            {added ? "✓" : <CartIcon />}
          </button>
        </div>
      </div>

      <style jsx>{`
        .product-card {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #ede5d8;
          transition: transform 0.25s, box-shadow 0.25s;
          cursor: pointer;
          height: 100%;
        }
        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(61,43,22,0.12);
        }
        .product-card:hover .product-photo {
          transform: scale(1.05);
        }
        .product-image {
          width: 100%;
          aspect-ratio: 1;
          background: linear-gradient(135deg, #f0e8d8, #e0d0b8);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .product-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.4s ease;
        }
        .image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .img-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, rgba(201,169,110,0.2) 0%, transparent 70%);
        }
        .tea-emoji {
          font-size: 64px;
          filter: drop-shadow(0 4px 12px rgba(0,0,0,0.15));
          z-index: 1;
        }
        .product-info {
          padding: 12px 14px;
        }
        .product-name {
          font-family: 'Be Vietnam Pro', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #2c2218;
          margin: 0 0 8px;
          line-height: 1.4;
          min-height: 36px;
          overflow-wrap: anywhere;
        }
        .product-footer {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 8px;
        }
        .product-meta {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .product-price {
          font-size: 14px;
          font-weight: 700;
          color: #3d6b4f;
          font-family: 'Be Vietnam Pro', sans-serif;
          min-width: 0;
          overflow-wrap: anywhere;
        }
        .product-stock {
          font-size: 12px;
          font-weight: 600;
          color: #7d735f;
          font-family: 'Be Vietnam Pro', sans-serif;
        }
        .product-stock.out {
          color: #a14b4b;
        }
        .add-btn {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          border: 1.5px solid #c9a96e;
          background: #faf7f2;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 2px;
          flex: 0 0 40px;
          transition: background 0.2s, border-color 0.2s;
        }
        .add-btn:disabled {
          border-color: #c8bfae;
          color: #c8bfae;
          background: #faf7f2;
          cursor: not-allowed;
          opacity: 0.6;
        }
        .add-btn:hover {
          background: #3d6b4f;
          border-color: #3d6b4f;
          color: white;
        }
        .add-btn:disabled:hover {
          background: #faf7f2;
          border-color: #c8bfae;
          color: #c8bfae;
        }
        .add-btn.added {
          background: #3d6b4f;
          border-color: #3d6b4f;
          color: white;
        }
        @media (max-width: 640px) {
          .product-info {
            padding: 10px;
          }
          .product-name {
            font-size: 12px;
            min-height: 34px;
          }
          .product-price {
            font-size: 13px;
          }
          .add-btn {
            width: 38px;
            height: 38px;
            flex: 0 0 38px;
          }
        }
        @media (max-width: 360px) {
          .product-footer {
            align-items: flex-start;
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}
